// 05n §4 — THE TAP ABOVE THE CLEANUP. What an importer does with a spreadsheet
// cell that is supposed to name a taxonomy entry.
//
// THE PROBLEM, in brief 23 §4's words: "Unless the importers validate, the next
// import undoes 05n." Five call sites take `String(vals[idx] || "").trim()` and
// write it straight into a column this slice has just made a controlled
// vocabulary. That is also where prod's 423 unmappable `person_title` rows came
// from — `Mr`, `Dhr`, `Mevr`, `Melle` are not values the app's own selects can
// produce, and the census shows they arrived together.
//
// WHAT IT DOES NOT DO, and the brief is explicit: "Refusing the whole import
// over one bad cell would be worse than today's behaviour, so whatever you
// choose has to still let a real spreadsheet through." A firm importing 254
// members from an association's roster cannot be told to go and fix row 187
// before anything lands.
//
// SO: RESOLVE, WRITE THE CODE, AND REPORT WHAT DID NOT RESOLVE.
//
//   · a cell that names exactly one entry, on any of the five facets, in any
//     language      -> the CODE is written. "Belgique", "Nederland" and "BE"
//                      all become `BE`, which is the whole point.
//   · an empty cell -> null. Not a default. `country: vals[i] || "Belgium"` is
//                      how 790 prod rows came to hold a country nobody chose.
//   · anything else -> THE CELL IS WRITTEN THROUGH, UNCHANGED, and reported.
//
// THAT LAST LINE IS THE JUDGEMENT AND IT IS DELIBERATE. The alternatives are
// worse in both directions. Dropping the value loses data the spreadsheet
// carried and the importer cannot get back. Guessing at the nearest entry is
// the thing this whole slice exists to prevent, and it would guess on every
// future import, silently, for ever — which is exactly why brief 23 §3 ruled
// the folds a TABLE and not a normaliser. Writing it through keeps the record,
// and 05m-b's provisional option means the row now OPENS SHOWING what it holds,
// marked, for a human to re-pick. The import is not the last chance to fix it;
// it is the first chance to SAY it needs fixing.
//
// The report is the other half and it is not optional: an unresolved cell that
// nobody is told about is today's behaviour with extra steps.
//
// PURE ESM. No React, no DOM, no network — the same shape as `coiMatch.mjs` and
// `importCells.mjs`, so the server can require it if a server-side importer ever
// needs the same rule.

import { claimants } from './taxonomyMatch.mjs';

/**
 * Resolve one spreadsheet cell against one kind's vocabulary.
 *
 * @param {any} cell        the raw cell, as read from the sheet
 * @param {ReadonlyArray<any>|null|undefined} entries  that kind's TaxonomyEntry
 *   rows (useTaxonomy(kind).entries). Nullable on purpose: a kind that has not
 *   hydrated is not evidence that a cell is wrong — see the branch below.
 * @returns {{ value: string|null, state: 'empty'|'resolved'|'unresolved'|'ambiguous', matched?: string }}
 *
 * `value` is ALWAYS what should be written. The caller does not branch on
 * `state` to decide what to store — only to decide what to report — because a
 * caller that has to remember which states mean "write it anyway" is a caller
 * that will forget.
 */
export function resolveImportCell(cell, entries) {
  const raw = String(cell ?? '').trim();
  if (raw === '') return { value: null, state: 'empty' };
  // An unloaded vocabulary is not evidence of anything. Writing the raw cell is
  // the honest answer, and the report says the import could not check.
  if (!Array.isArray(entries) || entries.length === 0) {
    return { value: raw, state: 'unresolved' };
  }
  const hits = claimants(raw, entries);
  if (hits.length === 1) return { value: hits[0].code, state: 'resolved', matched: hits[0].code };
  // TWO CLAIMANTS IS NOT A TIE TO BREAK, here least of all: `Mr.` is `mr`'s
  // labelEn and `me`'s labelNl, and an import has no idea which language the
  // sheet was written in. The cell goes through unchanged and is reported.
  return { value: raw, state: hits.length ? 'ambiguous' : 'unresolved' };
}

/**
 * Accumulates what an import could not resolve, so one toast can say it.
 *
 * Keyed by (kind, cell) rather than by row: a roster with 200 `Mr` cells is ONE
 * thing to tell somebody about, and 200 lines is a message nobody reads — the
 * same reason the census caps its named rows.
 *
 * The U+0000 separator below is WRITTEN AS AN ESCAPE and not as the character.
 * It shipped as the raw byte, and one NUL is all it takes for GNU grep to class
 * a file as BINARY and match nothing in it — silently, exit 1, no message. So
 * `grep -rn makeImportVocabularyReport src/` returned this function's three
 * CALL SITES and not its definition, and every grep-shaped instrument in this
 * tree read this file as empty. Same string at runtime; visible to the tools.
 */
export function makeImportVocabularyReport() {
  const seen = new Map();   // `${kind}\u0000${cell}` -> { kind, cell, state, rows: [] }
  return {
    note(kind, cell, state, excelRow) {
      if (state === 'empty' || state === 'resolved') return;
      const key = `${kind}\u0000${cell}`;
      if (!seen.has(key)) seen.set(key, { kind, cell, state, rows: [] });
      seen.get(key).rows.push(excelRow);
    },
    /** Distinct unresolved values, most rows first. */
    entries() {
      return [...seen.values()].sort((a, b) => b.rows.length - a.rows.length);
    },
    get size() { return seen.size; },
    get rowCount() { return [...seen.values()].reduce((n, e) => n + e.rows.length, 0); },
    /**
     * One line, in the caller's language. `t` is passed rather than imported so
     * this module stays free of the i18n runtime and can be driven by a test.
     */
    summary(t) {
      if (!seen.size) return null;
      const worst = this.entries().slice(0, 3)
        .map((e) => `${JSON.stringify(e.cell)} (${e.rows.length})`)
        .join(', ');
      // 208 §20 [RULED 16] — one sentence per number (the cells, the values), each in its language's plural
      return [
        t('import.vocab.unresolved_cells', {
          count: this.rowCount,
          values: worst,
          fallback: `${this.rowCount} imported cells did not match a list entry and were kept as written — ${worst}.`,
        }),
        t('import.vocab.unresolved_values', {
          count: seen.size,
          fallback: `${seen.size} different values in all. Open those records to pick a current value.`,
        }),
      ].join(' ');
    },
  };
}
